import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import {
  CANONICAL_CONSOLE_ROUTE_PATHS,
  CANONICAL_PROJECT_ROUTE_PATHS,
  parseArtifactRouteParams,
  parseChatRouteParams,
  parseConsoleApiProjectRouteParams,
  parseFileRouteParams,
  parseProjectItemRouteSearch,
  parseProjectRouteParams,
  parseWorkspaceRouteParams,
  stringifyArtifactRouteParams,
  stringifyChatRouteParams,
  stringifyConsoleApiProjectRouteParams,
  stringifyFileRouteParams,
  stringifyProjectRouteParams,
  stringifyWorkspaceRouteParams,
} from "./routes/canonicalRouteParams";
import { parseProjectSettingsRouteParams } from "./routes/projectSettingsRouteParams";
import { SETTINGS_SECTIONS } from "./routes/settingsCatalog";

const importWorkspacePage = () => import("./routes/WorkspacePage");
const WorkspacePage = lazyRouteComponent(importWorkspacePage, "WorkspacePage");
const WorkspaceProjectsPage = lazyRouteComponent(importWorkspacePage, "WorkspaceProjectsPage");
const WorkspaceProjectPage = lazyRouteComponent(importWorkspacePage, "WorkspaceProjectPage");
const WorkspaceChatPage = lazyRouteComponent(importWorkspacePage, "WorkspaceChatPage");
const WorkspaceArtifactPage = lazyRouteComponent(importWorkspacePage, "WorkspaceArtifactPage");
const WorkspaceFilePage = lazyRouteComponent(importWorkspacePage, "WorkspaceFilePage");
const ConsolePage = lazyRouteComponent(() => import("./routes/ConsolePage"), "ConsolePage");
const importConsoleRoutePage = () => import("./routes/ConsoleRoutePage");
const ConsoleOverviewRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleOverviewRoutePage",
);
const ConsoleProjectsRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleProjectsRoutePage",
);
const ConsoleProjectRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleProjectRoutePage",
);
const ConsoleApiKeysRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleApiKeysRoutePage",
);
const ConsoleUsageRoutePage = lazyRouteComponent(importConsoleRoutePage, "ConsoleUsageRoutePage");
const ConsoleLogsRoutePage = lazyRouteComponent(importConsoleRoutePage, "ConsoleLogsRoutePage");
const ConsoleModelsRoutePage = lazyRouteComponent(importConsoleRoutePage, "ConsoleModelsRoutePage");
const ConsoleWebhooksRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleWebhooksRoutePage",
);
const ConsoleTeamRoutePage = lazyRouteComponent(importConsoleRoutePage, "ConsoleTeamRoutePage");
const ConsoleBillingRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleBillingRoutePage",
);
const ConsoleSettingsRoutePage = lazyRouteComponent(
  importConsoleRoutePage,
  "ConsoleSettingsRoutePage",
);
const importSettingsPage = () => import("./routes/SettingsPage");
const GeneralSettingsPage = lazyRouteComponent(importSettingsPage, "GeneralSettingsPage");
const AppearanceSettingsPage = lazyRouteComponent(importSettingsPage, "AppearanceSettingsPage");
const KeybindingsSettingsPage = lazyRouteComponent(importSettingsPage, "KeybindingsSettingsPage");
const ConnectionsSettingsPage = lazyRouteComponent(importSettingsPage, "ConnectionsSettingsPage");
const importProjectSettingsPage = () => import("./routes/ProjectSettingsPage");
const ProjectSettingsPage = lazyRouteComponent(importProjectSettingsPage, "ProjectSettingsPage");
const ProjectSettingsError = lazyRouteComponent(importProjectSettingsPage, "ProjectSettingsError");

function RootLayout() {
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: RootLayout });
const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: WorkspacePage,
});
const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/console",
  component: ConsolePage,
});
const workspaceProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_PROJECT_ROUTE_PATHS.projects,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: WorkspaceProjectsPage,
});
const workspaceProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_PROJECT_ROUTE_PATHS.project,
  params: {
    parse: parseProjectRouteParams,
    stringify: stringifyProjectRouteParams,
  },
  component: WorkspaceProjectPage,
});
const workspaceChatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_PROJECT_ROUTE_PATHS.chat,
  params: {
    parse: parseChatRouteParams,
    stringify: stringifyChatRouteParams,
  },
  component: WorkspaceChatPage,
});
const workspaceArtifactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_PROJECT_ROUTE_PATHS.artifact,
  params: {
    parse: parseArtifactRouteParams,
    stringify: stringifyArtifactRouteParams,
  },
  validateSearch: parseProjectItemRouteSearch,
  component: WorkspaceArtifactPage,
});
const workspaceFileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_PROJECT_ROUTE_PATHS.file,
  params: {
    parse: parseFileRouteParams,
    stringify: stringifyFileRouteParams,
  },
  validateSearch: parseProjectItemRouteSearch,
  component: WorkspaceFilePage,
});
const consoleWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.overview,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleOverviewRoutePage,
});
const consoleProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.projects,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleProjectsRoutePage,
});
const consoleProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.project,
  params: {
    parse: parseConsoleApiProjectRouteParams,
    stringify: stringifyConsoleApiProjectRouteParams,
  },
  component: ConsoleProjectRoutePage,
});
const consoleApiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.apiKeys,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleApiKeysRoutePage,
});
const consoleUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.usage,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleUsageRoutePage,
});
const consoleLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.logs,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleLogsRoutePage,
});
const consoleModelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.models,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleModelsRoutePage,
});
const consoleWebhooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.webhooks,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleWebhooksRoutePage,
});
const consoleTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.team,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleTeamRoutePage,
});
const consoleBillingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.billing,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleBillingRoutePage,
});
const consoleSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CANONICAL_CONSOLE_ROUTE_PATHS.settings,
  params: {
    parse: parseWorkspaceRouteParams,
    stringify: stringifyWorkspaceRouteParams,
  },
  component: ConsoleSettingsRoutePage,
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: () => redirect({ to: SETTINGS_SECTIONS.general.path, replace: true }),
});
const settingsGeneralRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SETTINGS_SECTIONS.general.path,
  component: GeneralSettingsPage,
});
const settingsAppearanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SETTINGS_SECTIONS.appearance.path,
  component: AppearanceSettingsPage,
});
const settingsKeybindingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SETTINGS_SECTIONS.keybindings.path,
  component: KeybindingsSettingsPage,
});
const settingsConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SETTINGS_SECTIONS.connections.path,
  component: ConnectionsSettingsPage,
});
const projectSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId/projects/$projectId/settings",
  params: {
    parse: parseProjectSettingsRouteParams,
    stringify: ({ projectId, workspaceId }) => ({ projectId, workspaceId }),
  },
  component: ProjectSettingsPage,
  errorComponent: ProjectSettingsError,
});

const routeTree = rootRoute.addChildren([
  workspaceRoute,
  consoleRoute,
  workspaceProjectsRoute,
  workspaceProjectRoute,
  workspaceChatRoute,
  workspaceArtifactRoute,
  workspaceFileRoute,
  consoleWorkspaceRoute,
  consoleProjectsRoute,
  consoleProjectRoute,
  consoleApiKeysRoute,
  consoleUsageRoute,
  consoleLogsRoute,
  consoleModelsRoute,
  consoleWebhooksRoute,
  consoleTeamRoute,
  consoleBillingRoute,
  consoleSettingsRoute,
  settingsRoute,
  settingsGeneralRoute,
  settingsAppearanceRoute,
  settingsKeybindingsRoute,
  settingsConnectionsRoute,
  projectSettingsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
